// ─────────────────────────────────────────────────────────────────────────────
// GuideLayout — shared shell and content primitives for every /guide/* page
//
// Single source of truth for guide visual language. Every page wraps its
// content in <GuideLayout title="..." prev={...} next={...}> and uses the
// primitives below (H2, P, Field, ScreenshotFrame, Callout, etc) so the
// reading experience is consistent across the whole reference.
//
// Pages reference screenshots from /guide/<slug>.png in the public folder.
// ─────────────────────────────────────────────────────────────────────────────

import { Link } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";

// ─── Layout shell ───────────────────────────────────────────────────────────
//
// title:    page title, large at top
// subtitle: optional one-line subtitle under the title
// prev/next: { slug, label } for footer navigation
// children: page body

export function GuideLayout({ title, subtitle, prev, next, children }) {
  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 20 }}>
      {/* Breadcrumb / back to index */}
      <div style={{ marginBottom: 18 }}>
        <Link
          to="/guide"
          style={{ fontSize: 12, color: C.muted, textDecoration: "none" }}
        >
          ← Guide
        </Link>
      </div>

      {/* Page header */}
      <header style={{ marginBottom: 24 }}>
        <h1 style={{
          fontSize: 26,
          fontWeight: 800,
          color: C.text,
          margin: "0 0 6px",
          letterSpacing: "-0.02em",
        }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontSize: 14, color: C.muted, margin: 0, lineHeight: 1.5 }}>
            {subtitle}
          </p>
        )}
      </header>

      {/* Body */}
      <Card style={{ padding: "24px 28px" }}>
        {children}
      </Card>

      {/* Prev / next navigation */}
      <nav style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        marginTop: 24,
        marginBottom: 40,
      }}>
        {prev ? <NavTile direction="prev" {...prev} /> : <div />}
        {next ? <NavTile direction="next" {...next} /> : <div />}
      </nav>
    </div>
  );
}

function NavTile({ direction, slug, label }) {
  const isPrev = direction === "prev";
  return (
    <Link
      to={`/guide/${slug}`}
      style={{
        display: "block",
        padding: "14px 16px",
        borderRadius: 10,
        border: `1px solid ${C.border}`,
        background: C.panel,
        textDecoration: "none",
        textAlign: isPrev ? "left" : "right",
      }}
    >
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
        {isPrev ? "← Previous" : "Next →"}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
        {label}
      </div>
    </Link>
  );
}

// ─── Content primitives ─────────────────────────────────────────────────────

export function H2({ children }) {
  return (
    <h2 style={{
      fontSize: 18,
      fontWeight: 800,
      color: C.text,
      margin: "32px 0 12px",
      letterSpacing: "-0.01em",
    }}>
      {children}
    </h2>
  );
}

// First H2 on a page shouldn't have the big top margin — use this variant
export function H2First({ children }) {
  return (
    <h2 style={{
      fontSize: 18,
      fontWeight: 800,
      color: C.text,
      margin: "0 0 12px",
      letterSpacing: "-0.01em",
    }}>
      {children}
    </h2>
  );
}

export function H3({ children }) {
  return (
    <h3 style={{
      fontSize: 14,
      fontWeight: 700,
      color: C.text,
      margin: "20px 0 8px",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}>
      {children}
    </h3>
  );
}

export function P({ children, style = {} }) {
  return (
    <p style={{
      fontSize: 14,
      lineHeight: 1.7,
      color: C.sub,
      margin: "0 0 14px",
      ...style,
    }}>
      {children}
    </p>
  );
}

export function UL({ children }) {
  return (
    <ul style={{
      fontSize: 14,
      lineHeight: 1.8,
      color: C.sub,
      paddingLeft: 22,
      margin: "0 0 14px",
    }}>
      {children}
    </ul>
  );
}

export function LI({ children }) {
  return <li style={{ marginBottom: 6 }}>{children}</li>;
}

// Inline pill for field/setting names (e.g. <Field>stop_loss</Field>)
export function Field({ children }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "1px 7px",
      borderRadius: 4,
      background: C.border + "40",
      color: C.text,
      fontFamily: "monospace",
      fontSize: 12.5,
      fontWeight: 600,
      letterSpacing: "-0.01em",
    }}>
      {children}
    </span>
  );
}

// Bold inline emphasis on a UI element / name (e.g. "Click the <UI>Save</UI> button")
export function UI({ children }) {
  return (
    <strong style={{ color: C.text, fontWeight: 700 }}>{children}</strong>
  );
}

// ─── Screenshot ─────────────────────────────────────────────────────────────
//
// src:     path under /public, e.g. "/guide/dashboard.png"
// alt:     accessibility text
// caption: optional one-line caption shown beneath the image
//
// Falls back to a placeholder block if the image fails to load, so missing
// screenshots don't break the page during the screenshot-capture pass.

export function ScreenshotFrame({ src, alt, caption }) {
  return (
    <figure style={{ margin: "20px 0 24px", padding: 0 }}>
      <div style={{
        background: "#0b0d19",
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: 8,
        overflow: "hidden",
      }}>
        <img
          src={src}
          alt={alt}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            borderRadius: 6,
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.nextElementSibling.style.display = "block";
          }}
        />
        <div style={{
          display: "none",
          padding: "60px 20px",
          textAlign: "center",
          color: C.muted,
          fontSize: 12,
          fontStyle: "italic",
        }}>
          [Screenshot pending: {alt}]
        </div>
      </div>
      {caption && (
        <figcaption style={{
          fontSize: 12,
          color: C.muted,
          marginTop: 8,
          textAlign: "center",
          fontStyle: "italic",
        }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// ─── Callouts ───────────────────────────────────────────────────────────────
//
// Three flavors:
//   info    — neutral, "good to know" context
//   tip     — practical advice the user can act on
//   warn    — something to watch out for or a common pitfall

export function Callout({ kind = "info", title, children }) {
  const styles = {
    info: { border: C.blue,   bg: C.blue + "10",   color: C.blue,   icon: "ℹ️" },
    tip:  { border: C.green,  bg: C.green + "10",  color: C.green,  icon: "💡" },
    warn: { border: C.yellow, bg: C.yellow + "10", color: C.yellow, icon: "⚠️" },
  };
  const s = styles[kind] || styles.info;
  return (
    <div style={{
      borderLeft: `3px solid ${s.border}`,
      background: s.bg,
      borderRadius: 6,
      padding: "12px 14px",
      margin: "16px 0 20px",
      fontSize: 13.5,
      lineHeight: 1.65,
      color: C.sub,
    }}>
      {title && (
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: s.color,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 6,
        }}>
          <span style={{ marginRight: 6 }}>{s.icon}</span>{title}
        </div>
      )}
      <div>{children}</div>
    </div>
  );
}
